import React, { createContext, useContext, useState, useCallback } from 'react';

interface AudioStreamsState {
  drawerOpen: boolean;
  hasBeenOpened: boolean; // Track if drawer was opened this session (widgets are loaded)
}

interface AudioStreamsContextValue extends AudioStreamsState {
  toggleDrawer: () => void;
  setDrawerOpen: (open: boolean) => void;
}

const AudioStreamsContext = createContext<AudioStreamsContextValue | null>(null);

const defaultState: AudioStreamsState = {
  drawerOpen: false,
  hasBeenOpened: false,
};

export function AudioStreamsProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AudioStreamsState>(defaultState);

  const toggleDrawer = useCallback(() => {
    setState((prev) => ({
      ...prev,
      drawerOpen: !prev.drawerOpen,
      hasBeenOpened: true,
    }));
  }, []);

  const setDrawerOpen = useCallback((open: boolean) => {
    setState((prev) => ({
      ...prev,
      drawerOpen: open,
      hasBeenOpened: open ? true : prev.hasBeenOpened,
    }));
  }, []);

  return (
    <AudioStreamsContext.Provider
      value={{
        ...state,
        toggleDrawer,
        setDrawerOpen,
      }}
    >
      {children}
    </AudioStreamsContext.Provider>
  );
}

export function useAudioStreams() {
  const context = useContext(AudioStreamsContext);
  if (!context) {
    throw new Error('useAudioStreams must be used within an AudioStreamsProvider');
  }
  return context;
}
